import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Calendar, Trash2, Palette, ArrowRight, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import axios from "axios";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const History = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/analyses`, { withCredentials: true });
      setAnalyses(response.data.analyses || []);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load history");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAnalysis = async (e, analysisId) => {
    e.stopPropagation(); // Prevent card click
    setDeletingId(analysisId);
    try {
      await axios.delete(`${API}/analyses/${analysisId}`, { withCredentials: true });
      setAnalyses(prev => prev.filter(a => a.analysis_id !== analysisId));
      toast.success("Analysis deleted");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const openAnalysis = (analysis) => {
    // Navigate to dashboard with the saved analysis data
    navigate("/dashboard", { 
      state: { 
        savedAnalysis: {
          analysis_id: analysis.analysis_id,
          detected_items: analysis.detected_items,
          created_at: analysis.created_at
        }
      } 
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-primary)' }} data-testid="history-page">
      <main className="container py-12">
        <div className="max-w-5xl mx-auto">
          
          {/* Page Header */}
          <div className="mb-12 animate-fade-in">
            <p className="text-meta uppercase tracking-widest mb-4" style={{ color: 'var(--accent-primary)' }}>
              Your Collection
            </p>
            <h1 className="text-display mb-4" style={{ color: 'var(--text-primary)' }}>
              Saved Palettes
            </h1>
            <p className="text-body text-secondary max-w-lg">
              Your history of analyzed garments and extracted color palettes. Click any analysis to continue searching for matching items.
            </p>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-shimmer w-32 h-1 rounded-full"></div>
            </div>
          ) : analyses.length === 0 ? (
            <div className="text-center py-20 animate-fade-in">
              <div 
                className="w-24 h-24 mx-auto mb-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--surface-secondary)' }}
              >
                <Palette className="w-10 h-10" style={{ color: 'var(--text-muted)' }} />
              </div>
              <h3 className="text-h3 mb-3" style={{ color: 'var(--text-primary)' }}>
                No analyses yet
              </h3>
              <p className="text-body text-secondary mb-8 max-w-sm mx-auto">
                Start analyzing clothing images to build your palette collection.
              </p>
              <Link to="/dashboard" className="btn-primary" data-testid="start-analyzing-btn">
                Analyze First Image
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
              {analyses.map((analysis, index) => (
                <div
                  key={analysis.analysis_id}
                  onClick={() => openAnalysis(analysis)}
                  className="card group relative cursor-pointer hover:shadow-lg transition-all"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`analysis-card-${index}`}
                >
                  {/* Delete Button */}
                  <button
                    onClick={(e) => deleteAnalysis(e, analysis.analysis_id)}
                    disabled={deletingId === analysis.analysis_id}
                    className="absolute top-4 right-4 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all z-10"
                    style={{ backgroundColor: 'var(--surface-secondary)' }}
                    data-testid={`delete-analysis-${index}`}
                  >
                    {deletingId === analysis.analysis_id ? (
                      <div className="animate-shimmer w-4 h-4 rounded"></div>
                    ) : (
                      <Trash2 className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                    )}
                  </button>

                  {/* Date */}
                  <div className="flex items-center gap-2 mb-6">
                    <Calendar className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                    <span className="text-meta text-muted">
                      {formatDate(analysis.created_at)}
                    </span>
                  </div>

                  {/* Detected Items */}
                  <div className="space-y-6">
                    {analysis.detected_items.map((item, itemIndex) => (
                      <div 
                        key={itemIndex} 
                        className="pb-6 last:pb-0"
                        style={{ borderBottom: itemIndex < analysis.detected_items.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}
                      >
                        <span className="text-meta uppercase tracking-widest block mb-1" style={{ color: 'var(--accent-primary)' }}>
                          {item.category}
                        </span>
                        <h3 className="text-body font-medium mb-4" style={{ color: 'var(--text-primary)' }}>
                          {item.name}
                        </h3>
                        
                        {/* Color Swatches */}
                        <div className="flex gap-2">
                          {item.colors.slice(0, 4).map((color, colorIndex) => (
                            <div 
                              key={colorIndex}
                              className="w-10 h-10 rounded-lg transition-transform hover:scale-105"
                              style={{ 
                                backgroundColor: color.hex, 
                                border: '1px solid var(--border-subtle)',
                                boxShadow: 'var(--shadow-sm)'
                              }}
                              title={`${color.pantone_name || color.name} - ${color.hex}`}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Continue Search CTA */}
                  <div 
                    className="mt-6 pt-4 flex items-center justify-between"
                    style={{ borderTop: '1px solid var(--border-subtle)' }}
                  >
                    <span className="text-small flex items-center gap-2" style={{ color: 'var(--accent-primary)' }}>
                      <Search className="w-4 h-4" />
                      Continue searching
                    </span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" style={{ color: 'var(--accent-primary)' }} />
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default History;
